import React, { useState, useEffect } from 'react';
import { ArrowLeftOutlined, UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, CalendarOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Card, Row, Col, Form, Input, Button, Alert, Avatar, Typography, Descriptions } from 'antd';
import API from '../../../API';
import { notifySuccess, notifyError } from '../../../Util';

const { Title, Text } = Typography;

const Account = () => {
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', content: '' });
    const [passwordValidation, setPasswordValidation] = useState({ isValid: false, requirements: [] });
    const [form] = Form.useForm();

    useEffect(() => {
        loadUserProfile();
    }, []);

    const loadUserProfile = () => {
        try {
            // Get user from localStorage first
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const userData = JSON.parse(storedUser);
                setUser(userData);
            }
            
            // Optionally fetch fresh data from API
            API.getAuthUser()
                .then((response) => {
                    setUser(response.data || response);
                })
                .catch((error) => {
                    console.warn('Could not fetch fresh user data:', error);
                    // Continue with stored user data if API fails
                });
        } catch (error) {
            console.error('Error loading user profile:', error);
            notifyError('Failed to load user profile');
        }
    };

    const handleBack = () => {
        history.goBack();
    };

    const handlePasswordChange = (password) => {
        if (password) {
            const validation = API.validatePassword(password);
            setPasswordValidation(validation);
        } else {
            setPasswordValidation({ isValid: false, requirements: [] });
        }
    };

    const onPasswordChangeFinish = async (values) => {
        setPasswordLoading(true);
        setMessage({ type: '', content: '' });

        try {
            await API.changePassword({
                current_password: values.currentPassword,
                new_password: values.newPassword,
                new_password_confirmation: values.confirmPassword
            });

            setMessage({ 
                type: 'success', 
                content: 'Password changed successfully!' 
            });
            
            // Reset form
            form.resetFields();
            setPasswordValidation({ isValid: false, requirements: [] });
            notifySuccess('Password changed successfully!');
            
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.errors?.current_password?.[0] ||
                               error.response?.data?.errors?.new_password?.[0] ||
                               'Failed to change password. Please try again.';
            
            setMessage({ type: 'error', content: errorMessage });
            notifyError(errorMessage);
        } finally {
            setPasswordLoading(false);
        }
    };

    const getUserInitials = (user) => {
        if (!user) return 'U';
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U';
    };

    const getUserDisplayName = (user) => {
        if (!user) return 'User';
        return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
    };

    if (!user) {
        return (
            <div className="account-settings-container">
                <div className="account-settings-header">
                    <button onClick={handleBack} className="ActionBar-backButton">
                        <ArrowLeftOutlined />
                        <span className="ActionBar-backButton-text">Back</span>
                    </button>
                </div>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    Loading user profile...
                </div>
            </div>
        );
    }

    return (
        <div className="account-settings-container" style={{padding: "16px"}}>
            <div className="account-settings-header">
                <button onClick={handleBack} className="ActionBar-backButton">
                    <ArrowLeftOutlined />
                    <span className="ActionBar-backButton-text">Back</span>
                </button>
            </div>
            
            <div className="account-settings" style={{ padding: '24px' }}>
                <Title level={2}>Account Settings</Title>
                <Text type="secondary">Manage your personal account settings and security</Text>
                
                <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                    {/* User Profile Section */}
                    <Col xs={24} lg={12}>
                        <Card title="Profile Information" bordered={false}>
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                <Avatar size={80} style={{ backgroundColor: '#1890ff', fontSize: '32px' }}>
                                    {getUserInitials(user)}
                                </Avatar>
                                <Title level={4} style={{ marginTop: '12px', marginBottom: '4px' }}>
                                    {getUserDisplayName(user)}
                                </Title>
                                <Text type="secondary">{user.email}</Text>
                            </div>
                            
                            <Descriptions column={1} bordered size="small">
                                <Descriptions.Item label={<><UserOutlined /> Full Name</>}>
                                    {getUserDisplayName(user)}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><MailOutlined /> Email</>}>
                                    {user.email || 'Not provided'}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><PhoneOutlined /> Phone</>}>
                                    {user.phone || 'Not provided'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Title">
                                    {user.title || 'Not provided'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Role">
                                    {user.roles?.map(role => role.name).join(', ') || 'No roles assigned'}
                                </Descriptions.Item>
                                {user.created_at && (
                                    <Descriptions.Item label={<><CalendarOutlined /> Member Since</>}>
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    </Col>
                    
                    {/* Change Password Section */}
                    <Col xs={24} lg={12}>
                        <Card title="Change Password" bordered={false}>
                            {message.content && (
                                <Alert
                                    message={message.content}
                                    type={message.type}
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                    closable
                                    onClose={() => setMessage({ type: '', content: '' })}
                                />
                            )}
                            
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onPasswordChangeFinish}
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="currentPassword"
                                    label="Current Password"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please input your current password!",
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Enter current password"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="newPassword"
                                    label="New Password"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please input your new password!",
                                        },
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve();
                                                const validation = API.validatePassword(value);
                                                return validation.isValid 
                                                    ? Promise.resolve() 
                                                    : Promise.reject(new Error('Password does not meet requirements'));
                                            }
                                        }
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Enter new password"
                                        onChange={(e) => handlePasswordChange(e.target.value)}
                                    />
                                </Form.Item>

                                {/* Password Requirements */}
                                {passwordValidation.requirements.length > 0 && (
                                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '6px' }}>
                                        <Text strong style={{ fontSize: '14px' }}>Password Requirements:</Text>
                                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                                            {passwordValidation.requirements.map((req) => (
                                                <li key={req.key} style={{ 
                                                    color: req.valid ? '#52c41a' : '#ff4d4f',
                                                    marginBottom: '4px'
                                                }}>
                                                    {req.valid ? '✓' : '✗'} {req.text}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <Form.Item
                                    name="confirmPassword"
                                    label="Confirm New Password"
                                    dependencies={['newPassword']}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please confirm your new password!",
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('newPassword') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The two passwords do not match!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Confirm new password"
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={passwordLoading}
                                        disabled={!passwordValidation.isValid}
                                        style={{ width: '100%' }}
                                    >
                                        Change Password
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Account; 